import { http } from '@/chassis/data/services/http-client';
import { representationsService, type RepresentationRecord } from '@/domains/pages/3.3-registration-module-representation/Representations/service';
import { publicTendersService, type PublicTenderRecord } from '@/domains/pages/4.1-publictender-module-request/PublicTenders/service';
import { proposalsService, type ProposalsRecord } from '@/domains/pages/4.4-publictender-module-bidding/Proposals/service';
import { publicTenderFollowsService } from '@/domains/pages/4.1-publictender-module-request/PublicTenderFollows/service';
import { citizensService, type CitizenRecord } from '@/domains/pages/3.1-registration-module-citizen/Citizens/service';
import { suppliersService } from '@/domains/pages/3.2-registration-module-company/Suppliers/service';
import { publicAgenciesService } from '@/domains/pages/3.2-registration-module-company/PublicAgencies/service';
import { userCredentialsService } from '@/domains/pages/2.1-auth-module-authentication/UserCredentials/service';
import { buildEntityCacheKey, normalizeResourceId, resolveRepresentationEntity, type RepresentationEntityType } from './entity-utils';
import { DataSanitizationUtils } from '@/chassis/core/utils/datasanitization-utils';
import { TRUTHY_STRINGS, FALSY_STRINGS, ACTIVE_STATUS_STRINGS, INACTIVE_STATUS_STRINGS } from '../constants';
import type { PortalDashboardOverview, RepresentationSummary, TenderSummary } from '../MyDashboard/service';
import { subscriptionsService, type SubscriptionRecord } from '@/domains/pages/5.3-subscription-module-order/Subscriptions/service';
import { subscriptionPlansService, type SubscriptionPlanRecord } from '@/domains/pages/5.3-subscription-module-order/SubscriptionPlans/service';

export type { SubscriptionPlanRecord };

export type RepresentationEntityInfo = {
    entityId: string;
    entityType: RepresentationEntityType;
    name?: string;
    secondaryName?: { label: string; value: string };
    document?: string;
};

type RepresentationsCache = { key: string; items: RepresentationSummary[] } | null;

let representationsCache: RepresentationsCache = null;
let inflightRepresentationsPromise: { key: string; promise: Promise<RepresentationSummary[]> } | null = null;

type CitizenCache = Record<string, CitizenRecord | null>;

let citizenByCpfCache: CitizenCache = {};
let inflightCitizenByCpf: Record<string, Promise<CitizenRecord | null>> = {};

type EntityInfoCache = Record<string, RepresentationEntityInfo>;
type EntityInflight = Record<string, Promise<RepresentationEntityInfo | null>>;

let supplierEntityCache: EntityInfoCache = {};
let publicAgencyEntityCache: EntityInfoCache = {};
let inflightSupplierEntity: EntityInflight = {};
let inflightPublicAgencyEntity: EntityInflight = {};

const computeActiveRepresentationTotal = (result: any): number | null => {
    const pagination = result?.pagination as any;
    const summary = result?.summary as any;
    const totalFromPagination = typeof pagination?.totalItems === 'number' ? pagination.totalItems : typeof pagination?.total === 'number' ? pagination.total : undefined;
    const totalFromSummary = typeof summary?.total === 'number' ? summary.total : undefined;
    const computedTotal = totalFromPagination ?? totalFromSummary ?? (Array.isArray(result?.items) ? result.items.length : undefined);
    return typeof computedTotal === 'number' && Number.isFinite(computedTotal) ? computedTotal : null;
};

const normalizeRepresentativeId = (raw?: string | null) => {
    const trimmed = (raw ?? '').trim();
    if (!trimmed) return undefined;
    // O portal/home consome representacoes com representativeUserId no formato USER:...
    // Remover esta normalizacao volta a zerar a lista de representacoes da home.
    return trimmed.startsWith('USER:') ? trimmed : `USER:${trimmed}`;
};

const buildRepCacheKey = (repId?: string | null, active?: boolean | null) => {
    const base = repId && repId.trim() ? `rep:${repId.trim()}` : 'all';
    if (active === true) return `${base}:active`; // explicit active only
    if (active === false) return `${base}:inactive`; // explicit inactive only (unlikely used)
    return `${base}:all`; // no filter (all)
};

type AuthContextUser = {
    id?: string;
    cpf?: string | null;
    email?: string | null;
    name?: string | null;
    isGuest?: boolean;
};

const activeRepresentation = (item: RepresentationSummary | undefined | null): boolean => {
    if (!item) return false;

    // normalize "active" field that may come as boolean or string variations
    if (typeof item.active === 'boolean') return item.active;
    if (item.active != null) {
        const normalized = item.active.toString().trim().toLowerCase();
        if ((TRUTHY_STRINGS as readonly string[]).includes(normalized)) return true;
        if ((FALSY_STRINGS as readonly string[]).includes(normalized)) return false;
    }

    // fallback to status field variants (backend sometimes returns status instead of active boolean)
    const status = (item.status ?? '').toString().trim().toUpperCase();
    if ((ACTIVE_STATUS_STRINGS as readonly string[]).includes(status)) return true;
    if ((INACTIVE_STATUS_STRINGS as readonly string[]).includes(status)) return false;

    return false;
};

const toRepresentationSummary = (record: RepresentationRecord) => {
    return {
        id: record.id,
        label: record.categoryId || record.representationType || record.id,
        active: record.active,
        representationType: record.representationType,
        status: record.status,
        validFrom: record.validFrom,
        validUntil: record.validUntil,
        grantedPowersCount: Array.isArray(record.grantedPowers) ? record.grantedPowers.length : 0,
        entitySupplierId: record.entitySupplierId ?? null,
        entityPublicAgencyId: record.entityPublicAgencyId ?? null,
    } satisfies RepresentationSummary;
};

/**
 * Resolve nomes dos orgaos contratantes a partir de seus IDs.
 * Segue o mesmo padrao usado em PublicTenders/search.tsx.
 */
const resolveAgencyNames = async (agencyIds: string[]): Promise<Map<string, string>> => {
    const unique = [...new Set(agencyIds.filter((id) => typeof id === 'string' && id.trim().length > 0))];
    const nameMap = new Map<string, string>();
    if (!unique.length) return nameMap;

    const stripPrefix = (id: string) => id.replace(/^(AGENCY|PUBLICAGENCY):/, '');
    const results = await Promise.allSettled(unique.map((id) => publicAgenciesService.get(stripPrefix(id))));
    results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value?.name) {
            nameMap.set(unique[index], result.value.name);
        }
    });
    return nameMap;
};

const toTenderSummary = (record: PublicTenderRecord) => {
    return {
        tenderId: record.id,
        tenderTitle: record.title,
        tenderStatus: record.status ?? record.statusKey,
        publicTenderCode: record.publicTenderCode ?? null,
        administrativeProcessCode: record.administrativeProcessCode ?? null,
        publishedDate: record.publishedDate ?? record.publishedAt ?? null,
        submissionDeadline: record.submissionDeadline ?? record.proposalSubmissionEndDate ?? null,
        disputeStartDate: record.disputeStartDate ?? record.openingDate ?? null,
        proposalId: undefined,
        proposalStatus: undefined,
        proposalValue: record.estimatedValue,
        ranking: undefined,
        lastUpdatedAt: record.updatedAt ?? null,
        contractingAgency: typeof record.contractingAgency === 'object'
            ? (record.contractingAgency as any)?.name ?? null
            : record.contractingAgency ?? null,
        contractType: record.contractType ?? null,
        judgementCriteria: record.judgementCriteria ?? null,
        modality: record.modality ?? null,
        estimatedValue: typeof record.estimatedValue === 'number' ? record.estimatedValue : null,
    } satisfies TenderSummary;
};

const resolveTenderDisplayTitle = (
    tenderInfo?: TenderSummary,
): string => {
    const title = tenderInfo?.tenderTitle?.toString().trim();
    if (title) {
        return title;
    }

    const publicTenderCode = tenderInfo?.publicTenderCode?.toString().trim();
    if (publicTenderCode) {
        return `Licitacao ${publicTenderCode}`;
    }

    const administrativeProcessCode = tenderInfo?.administrativeProcessCode?.toString().trim();
    if (administrativeProcessCode) {
        return `Processo ${administrativeProcessCode}`;
    }

    return 'Licitacao monitorada';
};

export const userSharedService = {
    async findCitizenByCpf(cpf?: string | null): Promise<CitizenRecord | null> {
        const normalized = (cpf ?? '').trim();
        if (!normalized) {
            return null;
        }

        const key = normalized;

        if (key in citizenByCpfCache) {
            return citizenByCpfCache[key];
        }

        if (key in inflightCitizenByCpf) {
            return inflightCitizenByCpf[key];
        }

        inflightCitizenByCpf[key] = citizensService
            .list({ cpf: normalized, page: 1, limit: 1 })
            .then((result) => {
                const record = result?.items?.[0] ?? null;
                citizenByCpfCache[key] = record;
                return record;
            })
            .catch(() => {
                citizenByCpfCache[key] = null;
                return null;
            })
            .finally(() => {
                delete inflightCitizenByCpf[key];
            });

        return inflightCitizenByCpf[key];
    },

    async fetchSupplierEntity(id: string, options?: { force?: boolean }): Promise<RepresentationEntityInfo | null> {
        const key = id.trim();
        if (!key) return null;

        const force = options?.force === true;

        if (force) {
            delete supplierEntityCache[key];
            delete inflightSupplierEntity[key];
        }

        if (!force && supplierEntityCache[key]) {
            return supplierEntityCache[key];
        }

        if (!force && key in inflightSupplierEntity) {
            return inflightSupplierEntity[key];
        }

        inflightSupplierEntity[key] = suppliersService
            .get(key)
            .then((record) => {
                const companyName = record.companyName?.trim();
                const tradeName = record.tradeName?.trim();
                const primaryName = companyName || tradeName || undefined;
                const secondaryName = companyName && tradeName && companyName !== tradeName ? { label: 'Nome fantasia', value: tradeName } : undefined;

                const info: RepresentationEntityInfo = {
                    entityId: key,
                    entityType: 'supplier',
                    name: primaryName,
                    secondaryName,
                    document: record.cnpj ?? undefined,
                };
                supplierEntityCache[key] = info;
                return info;
            })
            .catch(() => null)
            .finally(() => {
                delete inflightSupplierEntity[key];
            });

        return inflightSupplierEntity[key];
    },

    async fetchPublicAgencyEntity(id: string, options?: { force?: boolean }): Promise<RepresentationEntityInfo | null> {
        const key = id.trim();
        if (!key) return null;

        const force = options?.force === true;

        if (force) {
            delete publicAgencyEntityCache[key];
            delete inflightPublicAgencyEntity[key];
        }

        if (!force && publicAgencyEntityCache[key]) {
            return publicAgencyEntityCache[key];
        }

        if (!force && key in inflightPublicAgencyEntity) {
            return inflightPublicAgencyEntity[key];
        }

        inflightPublicAgencyEntity[key] = publicAgenciesService
            .get(key)
            .then((record) => {
                const name = record.name?.trim();
                const info: RepresentationEntityInfo = {
                    entityId: key,
                    entityType: 'public_agency',
                    name: name || undefined,
                    document: record.cnpj ?? undefined,
                };
                publicAgencyEntityCache[key] = info;
                return info;
            })
            .catch(() => null)
            .finally(() => {
                delete inflightPublicAgencyEntity[key];
            });

        return inflightPublicAgencyEntity[key];
    },

    async listRepresentationsForPortal(signal?: AbortSignal, options?: { force?: boolean; representativeUserId?: string | null; active?: boolean }): Promise<RepresentationSummary[]> {
        const force = options?.force === true;
        const representativeUserId = normalizeRepresentativeId(options?.representativeUserId || null) ?? null;
        const activeFilter = options?.active;
        const cacheKey = buildRepCacheKey(representativeUserId, activeFilter ?? null);

        if (!force && representationsCache?.key === cacheKey && representationsCache.items) {
            const activeCached = representationsCache.items.filter(activeRepresentation);
            if (activeCached.length !== representationsCache.items.length) {
                representationsCache = { key: cacheKey, items: activeCached };
            }
            return activeCached;
        }

        if (!force && inflightRepresentationsPromise?.key === cacheKey) {
            return inflightRepresentationsPromise.promise;
        }

        const promise = (async () => {
            try {
                const reps = await representationsService.list({ representativeUserId: representativeUserId || undefined, active: activeFilter, page: 1, limit: 100 });
                const repItems = Array.isArray(reps?.items) ? reps.items : [];
                const mapped = repItems.map(toRepresentationSummary);
                const active = mapped.filter(activeRepresentation);

                // Nao congelar o portal com cache de lista vazia: vazio pode ser estado transitorio.
                representationsCache = active.length ? { key: cacheKey, items: active } : null;
                return active;
            } finally {
                inflightRepresentationsPromise = null;
            }
        })();

        inflightRepresentationsPromise = { key: cacheKey, promise };
        return promise;
    },

    async listPublicTendersForPortal(signal?: AbortSignal, filters?: { contractingAgencyId?: string | null }): Promise<TenderSummary[]> {
        const contractingAgencyId = filters?.contractingAgencyId?.trim() || undefined;
        if (!contractingAgencyId) {
            return [];
        }
        const tenders = await publicTendersService.list({ active: true, page: 1, limit: 20, contractingAgencyId });
        const items = Array.isArray(tenders?.items) ? tenders.items : [];

        // Resolver nomes dos orgaos contratantes
        const agencyIds = items.map((item) => item?.contractingAgencyId).filter((id): id is string => typeof id === 'string' && id.length > 0);
        const agencyNameMap = await resolveAgencyNames(agencyIds);

        return items.map((item) => {
            const enriched = { ...item } as PublicTenderRecord;
            if (!enriched.contractingAgency && enriched.contractingAgencyId) {
                (enriched as any).contractingAgency = agencyNameMap.get(enriched.contractingAgencyId) ?? null;
            }
            return toTenderSummary(enriched);
        });
    },

    async listProposalsForPortal(signal?: AbortSignal, filters?: { supplierId?: string | null }): Promise<TenderSummary[]> {
        const supplierId = filters?.supplierId?.trim() || undefined;
        if (!supplierId) {
            return [];
        }
        const normalizedSupplierId = DataSanitizationUtils.ensureResourcePrefix('SUPPLIER', supplierId) ?? supplierId;
        const proposals = await proposalsService.list({ supplierId: normalizedSupplierId, page: 1, limit: 20 } as any);
        const items = Array.isArray(proposals?.items) ? proposals.items : [];

        const sanitizeTenderId = (raw?: string | null): { fetchId: string | null; apiId: string | null; displayId: string | null } => {
            const normalized = (raw ?? '').trim();
            if (!normalized) return { fetchId: null, apiId: null, displayId: null };

            // Remove query/hash
            const cleaned = normalized.split(/[?#]/)[0];
            if (!cleaned) return { fetchId: null, apiId: null, displayId: null };

            // Usa splitResourceId para separar PREFIX:value corretamente,
            // preservando o ID completo (incluindo sufixos como -mihwlpyb2x2v7)
            const split = DataSanitizationUtils.splitResourceId(cleaned);
            const core = split?.remainder || cleaned;
            if (!core) return { fetchId: null, apiId: null, displayId: null };

            const fetchId = cleaned;
            const apiId = core; // always unprefixed for API calls
            const displayId = DataSanitizationUtils.ensureResourcePrefix('PUBLICTENDER', core) ?? core;
            return { fetchId, apiId, displayId };
        };

        const tenderIds = Array.from(new Set(items.map((proposal) => sanitizeTenderId(proposal.tenderId).fetchId).filter(Boolean))) as string[];
        const tenderRecordById = new Map<string, any>();

        if (tenderIds.length) {
            await Promise.all(
                tenderIds.map(async (rawId) => {
                    try {
                        const details = await publicTendersService.details(rawId);
                        const tender = details?.tender;
                        if (tender) {
                            tenderRecordById.set(rawId, tender);
                        }
                    } catch (error) {
                        console.warn('Falha ao carregar licitação para proposta', { tenderId: rawId, error });
                    }
                })
            );
        }

        // Resolver nomes dos orgaos contratantes
        const agencyIds = [...tenderRecordById.values()]
            .map((t) => t?.contractingAgencyId)
            .filter((id): id is string => typeof id === 'string' && id.length > 0);
        const agencyNameMap = await resolveAgencyNames(agencyIds);

        const tenderById = new Map<string, TenderSummary>();
        for (const [rawId, tender] of tenderRecordById.entries()) {
            if (!tender.contractingAgency && tender.contractingAgencyId) {
                tender.contractingAgency = agencyNameMap.get(tender.contractingAgencyId) ?? null;
            }
            tenderById.set(rawId, toTenderSummary(tender));
        }

        return items
            .filter((proposal: ProposalsRecord) => {
                // Filtra propostas cujo tender não foi resolvido (ex.: tender deletado por seed cleanup)
                const { fetchId } = sanitizeTenderId(proposal.tenderId);
                return fetchId ? tenderById.has(fetchId) : false;
            })
            .map((proposal: ProposalsRecord): TenderSummary => {
                const { fetchId, apiId } = sanitizeTenderId(proposal.tenderId);
                const tenderInfo = fetchId ? tenderById.get(fetchId) : undefined;
                return {
                    tenderId: apiId ?? proposal.tenderId,
                    tenderTitle: resolveTenderDisplayTitle(tenderInfo),
                    tenderStatus: tenderInfo?.tenderStatus,
                    publicTenderCode: tenderInfo?.publicTenderCode ?? null,
                    administrativeProcessCode: tenderInfo?.administrativeProcessCode ?? null,
                    publishedDate: tenderInfo?.publishedDate ?? null,
                    submissionDeadline: tenderInfo?.submissionDeadline ?? null,
                    disputeStartDate: tenderInfo?.disputeStartDate ?? null,
                    proposalId: proposal.id,
                    proposalStatus: proposal.status,
                    proposalValue: typeof proposal.value === 'number' ? proposal.value : undefined,
                    ranking: typeof proposal.ranking === 'number' ? proposal.ranking : undefined,
                    lastUpdatedAt: proposal.updatedAt ?? proposal.submittedAt ?? tenderInfo?.lastUpdatedAt ?? null,
                    contractingAgency: tenderInfo?.contractingAgency ?? null,
                    contractType: tenderInfo?.contractType ?? null,
                    judgementCriteria: tenderInfo?.judgementCriteria ?? null,
                    modality: tenderInfo?.modality ?? null,
                    estimatedValue: tenderInfo?.estimatedValue ?? null,
                } satisfies TenderSummary;
            });
    },

    /**
     * Licitações MONITORADAS (sininho) — fonte única, a mesma da busca de processos.
     * Lista os follows ativos (publicTenderFollowsService) e resolve os detalhes de
     * cada licitação para montar o TenderSummary exibido no painel.
     */
    async listFollowedTendersForPortal(signal?: AbortSignal, filters?: { supplierId?: string | null; citizenId?: string | null }): Promise<TenderSummary[]> {
        const supplierId = filters?.supplierId?.trim() || undefined;
        const citizenId = filters?.citizenId?.trim() || undefined;
        const normalizedSupplierId = supplierId
            ? (DataSanitizationUtils.ensureResourcePrefix('SUPPLIER', supplierId) ?? supplierId)
            : undefined;
        const normalizedCitizenId = citizenId
            ? (DataSanitizationUtils.ensureResourcePrefix('CITIZEN', citizenId) ?? citizenId)
            : undefined;

        // Sem escopo (nem fornecedor nem cidadão) NÃO listamos nada: uma query sem
        // filtro traria follows de todos os perfis/usuários (vazamento entre perfis).
        if (!normalizedSupplierId && !normalizedCitizenId) {
            return [];
        }

        const resp = await publicTenderFollowsService.list({
            ...(normalizedSupplierId ? { supplierId: normalizedSupplierId } : {}),
            ...(normalizedCitizenId ? { citizenId: normalizedCitizenId } : {}),
            limit: 200,
        } as any);
        const follows = Array.isArray((resp as any)?.items) ? (resp as any).items : [];

        // Soft-delete: ignora follows inativos (deixados de monitorar).
        const activeFollows = follows.filter((f: any) => f?.active !== false && f?.publicTenderId);

        const bareId = (raw?: string | null): string | null => {
            const v = (raw ?? '').trim();
            if (!v) return null;
            const split = DataSanitizationUtils.splitResourceId(v);
            return split?.remainder || v;
        };

        const uniqueTenderIds = Array.from(
            new Set(activeFollows.map((f: any) => bareId(f.publicTenderId)).filter(Boolean)),
        ) as string[];

        const tenderRecordById = new Map<string, any>();
        if (uniqueTenderIds.length) {
            await Promise.all(
                uniqueTenderIds.map(async (id) => {
                    try {
                        const details = await publicTendersService.details(id);
                        const tender = details?.tender;
                        if (tender) tenderRecordById.set(id, tender);
                    } catch (error) {
                        console.warn('Falha ao carregar licitação monitorada', { tenderId: id, error });
                    }
                }),
            );
        }

        const agencyIds = [...tenderRecordById.values()]
            .map((t) => t?.contractingAgencyId)
            .filter((id): id is string => typeof id === 'string' && id.length > 0);
        const agencyNameMap = await resolveAgencyNames(agencyIds);

        return uniqueTenderIds
            .filter((id) => tenderRecordById.has(id))
            .map((id) => {
                const tender = tenderRecordById.get(id);
                if (!tender.contractingAgency && tender.contractingAgencyId) {
                    tender.contractingAgency = agencyNameMap.get(tender.contractingAgencyId) ?? null;
                }
                const summary = toTenderSummary(tender);
                return { ...summary, supplierSizeBenefit: tender.supplierSizeBenefit ?? null } as TenderSummary;
            });
    },

    async loadDashboardData(
        authUser?: AuthContextUser | null,
        options?: {
            signal?: AbortSignal;
            force?: boolean;
            selectedRepresentationId?: string | null;
            representations?: RepresentationSummary[];
        }
    ): Promise<{
        user: PortalDashboardOverview['user'];
        representations: RepresentationSummary[];
        tenders: TenderSummary[];
        activeRepresentationCount: number;
    }> {
        const signal = options?.signal;
        const force = options?.force === true;
        const selectedRepresentationId = options?.selectedRepresentationId ?? null;
        const isGuestUser = authUser?.isGuest === true || authUser?.id === 'guest' || authUser?.id?.startsWith('guest:');

        if (isGuestUser) {
            return {
                user: {
                    id: authUser?.id ?? 'guest',
                    citizenId: null,
                    name: authUser?.name || 'Visitante',
                    email: authUser?.email ?? null,
                } satisfies PortalDashboardOverview['user'],
                representations: [],
                tenders: [],
                activeRepresentationCount: 0,
            };
        }

        // Proteção: não fazer chamada se userId for 'unknown-user' (fallback do session.ts)
        const shouldFetchCredential = authUser?.id && authUser.id !== 'unknown-user';
        const credential = shouldFetchCredential ? await userCredentialsService.get(authUser!.id!) : null;
        const citizen = await userSharedService.findCitizenByCpf(credential?.cpf ?? authUser?.cpf);

        const representativeUserId = authUser?.id ?? undefined;

        // Uma lista vazia fornecida pelo caller nao deve bloquear o fetch do servico.
        const providedRepresentations = Array.isArray(options?.representations) && options.representations.length > 0
            ? options.representations
            : null;
        const repsFromList = providedRepresentations ?? await userSharedService.listRepresentationsForPortal(signal, { force, representativeUserId, active: true });
        const representations = Array.isArray(repsFromList) ? repsFromList.filter(activeRepresentation) : [];

        const selectedRepresentation = selectedRepresentationId ? representations.find((rep) => rep.id === selectedRepresentationId) : null;

        const selectedBinding = selectedRepresentation ? resolveRepresentationEntity(selectedRepresentation) : null;

        let tenders: TenderSummary[] = [];
        if (selectedBinding?.type === 'supplier') {
            // Licitações monitoradas (sininho) — mesma fonte da busca de processos.
            const supplierId = normalizeResourceId('SUPPLIER', selectedBinding.id) ?? selectedRepresentation?.entitySupplierId ?? selectedBinding.id;
            const followed = await userSharedService.listFollowedTendersForPortal(signal, { supplierId });
            tenders = Array.isArray(followed) ? followed : [];
        } else if (selectedBinding?.type === 'public_agency') {
            const contractingAgencyId = normalizeResourceId('PUBLICAGENCY', selectedBinding.id) ?? selectedRepresentation?.entityPublicAgencyId ?? selectedBinding.id;
            const tendersFromList = await userSharedService.listPublicTendersForPortal(signal, { contractingAgencyId });
            tenders = Array.isArray(tendersFromList) ? tendersFromList : [];
        } else if (selectedRepresentationId === 'citizen-view' && citizen?.id) {
            // Perfil cidadão: monitoradas escopadas pelo citizenId (o cidadão não é
            // uma representação — vem do citizen resolvido pelo CPF do usuário).
            const followed = await userSharedService.listFollowedTendersForPortal(signal, { citizenId: citizen.id });
            tenders = Array.isArray(followed) ? followed : [];
        }

        const user = {
            id: authUser?.id ?? credential?.userId ?? 'me',
            citizenId: citizen?.id ?? null,
            name: citizen?.name || authUser?.name || undefined,
            email: authUser?.email ?? credential?.email ?? null,
        } satisfies PortalDashboardOverview['user'];

        return {
            user,
            representations,
            tenders,
            activeRepresentationCount: representations.length,
        };
    },

    listSupplierSubscriptions(supplierId: string, page = 1, limit = 20): Promise<{ items: SubscriptionRecord[]; total?: number }> {
        return subscriptionsService.list({ supplierId, page, limit } as any);
    },

    getSubscriptionPlan(planId: string): Promise<SubscriptionPlanRecord | null> {
        const id = String(planId || '').trim();
        if (!id) return Promise.resolve(null);
        const sanitized = id.includes(':') ? (id.split(':').pop() || '').trim() : id;
        if (!sanitized) return Promise.resolve(null);
        return subscriptionPlansService.get(sanitized);
    },

    async listRepresentedSuppliers(signal?: AbortSignal): Promise<string[]> {
        const representations = await userSharedService.listRepresentationsForPortal(signal);
        const suppliers = new Set<string>();
        for (const item of representations) {
            if (typeof item?.entitySupplierId === 'string' && item.entitySupplierId.trim()) {
                suppliers.add(item.entitySupplierId.trim());
            }
        }
        return Array.from(suppliers);
    },

    async getActiveRepresentationsCount(): Promise<number | null> {
        try {
            const result = await representationsService.list({
                status: 'ACTIVE',
                page: 1,
                limit: 1,
            });

            return computeActiveRepresentationTotal(result);
        } catch (error) {
            console.error('Erro ao carregar quantidade de representações ativas para o dashboard', error);
            return null;
        }
    },

    async fetchRepresentationEntities(
        summaries: RepresentationSummary[],
        existingCacheKeys: Iterable<string> = [],
        options?: { force?: boolean }
    ): Promise<Array<{ cacheKey: string; info: RepresentationEntityInfo }>> {
        const pending: Array<{ cacheKey: string; type: RepresentationEntityType; id: string }> = [];
        const seen = new Set<string>();
        const force = options?.force === true;

        if (!force) {
            for (const key of existingCacheKeys) {
                seen.add(key);
            }
        }

        for (const summary of summaries) {
            const resolved = resolveRepresentationEntity(summary);
            if (!resolved) {
                continue;
            }

            const cacheKey = buildEntityCacheKey(resolved.type, resolved.id);
            if (seen.has(cacheKey)) {
                continue;
            }

            seen.add(cacheKey);
            pending.push({ cacheKey, type: resolved.type, id: resolved.id });
        }

        if (!pending.length) {
            return [];
        }

        const results = await Promise.all(
            pending.map(async ({ cacheKey, type, id }) => {
                try {
                    const info = type === 'supplier'
                        ? await userSharedService.fetchSupplierEntity(id, { force })
                        : await userSharedService.fetchPublicAgencyEntity(id, { force });

                    if (!info) {
                        return null;
                    }

                    return { cacheKey, info } as const;
                } catch (error) {
                    console.error('Erro ao carregar detalhes da entidade vinculada à representação', { type, id }, error);
                    return null;
                }
            })
        );

        return results.filter((entry): entry is NonNullable<(typeof results)[number]> => Boolean(entry && entry.info));
    },

    /**
     * Limpa todos os caches de módulo relacionados ao usuário.
     * Deve ser chamado ao trocar de sessão (logout/login) para evitar
     * contaminação de dados entre usuários diferentes.
     */
    clearUserCaches(): void {
        representationsCache = null;
        inflightRepresentationsPromise = null;
        citizenByCpfCache = {};
        inflightCitizenByCpf = {};
        supplierEntityCache = {};
        publicAgencyEntityCache = {};
        inflightSupplierEntity = {};
        inflightPublicAgencyEntity = {};
    },
};
